/* Whitehouse product-tile wishlist and quotation controls.
 * Compatible with WHL product-page v20-v23 localStorage format.
 * Never submits or modifies Shopify carts or checkout.
 */
(() => {
  'use strict';
  if (window.WHLCollectionCardActions) return;
  window.WHLCollectionCardActions = { version: '1.0.0' };

  const WKEY = 'whl-wishlist-v20';
  const QKEY = 'whl-quote-list-v20';
  const $ = (selector, scope=document) => scope.querySelector(selector);
  const $$ = (selector, scope=document) => Array.from(scope.querySelectorAll(selector));
  const safeText = (value) => String(value == null ? '' : value).trim();
  const escapeHtml = (value) => safeText(value).replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  const read = (key) => {
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  };
  const write = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify(data)); return true; }
    catch (_) { alert('Unable to save on this device. Please enable browser storage.'); return false; }
  };
  const safeHref = (value) => {
    const url = safeText(value);
    if (url.startsWith('/') && !url.startsWith('//')) return url;
    try {
      const parsed = new URL(url, location.href);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
    } catch (_) {}
    return '#';
  };
  const productId = (node) => safeText(node?.dataset.whlProductId);
  let dialog, toastTimer, refreshQueued = false;

  function toast(message) {
    let el = $('.whl-card-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'whl-card-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3600);
  }

  function quoteCount() { return read(QKEY).reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0); }
  function syncButtons() {
    const saved = new Set(read(WKEY).map(item => String(item.productId)));
    $$('[data-whl-card-actions]').forEach(tile => {
      const wrapper = tile.closest('.card-wrapper, .product-card-wrapper');
      if (wrapper && tile.dataset.whlProductQuoteOnly === 'true') wrapper.classList.add('whl-is-quote-only');
      const button = $('[data-whl-card-wish]', tile);
      if (!button) return;
      const active = saved.has(productId(tile));
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', (active ? 'Remove ' : 'Add ') + tile.dataset.whlProductTitle + (active ? ' from wishlist' : ' to wishlist'));
      button.title = active ? 'Remove from wishlist' : 'Save to wishlist';
    });
    $$('[data-whl-card-wish-count]').forEach(el => { el.textContent = String(saved.size); });
    $$('[data-whl-card-quote-count]').forEach(el => { el.textContent = String(quoteCount()); });
  }
  function scheduleSync() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => { refreshQueued = false; syncButtons(); });
  }

  function toggleWishlist(tile) {
    const id = productId(tile);
    if (!id) return;
    const old = read(WKEY);
    const has = old.some(item => String(item.productId) === id);
    const list = has ? old.filter(item => String(item.productId) !== id) : old.concat({
      productId: id,
      title: tile.dataset.whlProductTitle,
      url: tile.dataset.whlProductUrl,
      image: tile.dataset.whlProductImage
    });
    if (!write(WKEY, list)) return;
    syncButtons();
    window.dispatchEvent(new CustomEvent('whl:wishlist-updated'));
    toast(has ? 'Removed from your wishlist.' : 'Saved to your wishlist on this device.');
    if (dialog?.open && dialog.dataset.mode === 'wishlist') renderDialog('wishlist');
  }

  function addQuote(tile) {
    const id = productId(tile);
    if (!id) return;
    const select = $('[data-whl-card-variant]', tile);
    const option = select?.selectedOptions?.[0];
    if (select && !select.value) { select.focus(); toast('Select an option to request a quotation.'); return; }
    const variantId = safeText(select ? select.value : tile.dataset.whlVariantId);
    if (!variantId) { toast('Unable to identify the selected option. Open the product page.'); return; }
    const title = safeText(tile.dataset.whlProductTitle);
    const variant = safeText(select ? option?.dataset.whlTitle : tile.dataset.whlVariantTitle) || 'Default Title';
    const sku = safeText(select ? option?.dataset.whlSku : tile.dataset.whlVariantSku);
    const key = `${id}:${variantId}`;
    const list = read(QKEY);
    const match = list.find(item => String(item.key) === key);
    if (match) match.quantity = Math.min(9999, (Number(match.quantity) || 0) + 1);
    else list.push({ key, productId: id, variantId, title, variant, sku, quantity: 1, url: tile.dataset.whlProductUrl });
    if (!write(QKEY, list)) return;
    syncButtons();
    window.dispatchEvent(new CustomEvent('whl:quote-updated'));
    toast('📄 Added to your quotation list.');
  }

  function ensureDialog() {
    if (dialog && dialog.isConnected) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'whl-card-dialog';
    dialog.setAttribute('aria-label', 'Whitehouse saved items and quotations');
    dialog.innerHTML = '<div class="whl-card-dialog-head"><h2 data-whl-dialog-title>Your saved items</h2><button type="button" class="whl-card-close" data-whl-dialog-close aria-label="Close">×</button></div><div class="whl-card-list" data-whl-dialog-list></div><div class="whl-card-dialog-actions" data-whl-dialog-actions></div><p class="whl-card-dialog-note" data-whl-dialog-note></p>';
    document.body.appendChild(dialog);
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    return dialog;
  }

  function quoteEmail() {
    const item = $('[data-whl-card-actions][data-whl-store-email]');
    return safeText(item?.dataset.whlStoreEmail);
  }
  function quoteBody() {
    return 'Hello Whitehouse Lighting & Electrical,\n\nPlease provide a quotation for these items, including availability and delivery charges:\n\n' +
      read(QKEY).map((x, i) => `${i + 1}. ${x.title}\n   Option: ${x.variant || 'Standard'}\n   SKU: ${x.sku || 'Not provided'}\n   Quantity: ${x.quantity}\n   Product: ${new URL(safeHref(x.url), location.origin === 'null' ? 'https://www.whitehouselightingandelectrical.co.za' : location.origin).href}`).join('\n\n') +
      '\n\nMy name:\nDelivery area:\nContact number:\n';
  }
  function renderDialog(mode) {
    const el = ensureDialog();
    el.dataset.mode = mode;
    const items = read(mode === 'wishlist' ? WKEY : QKEY);
    $('[data-whl-dialog-title]', el).textContent = mode === 'wishlist' ? `Saved products (${items.length})` : `Quotation list (${quoteCount()} items)`;
    const list = $('[data-whl-dialog-list]', el);
    list.innerHTML = items.length ? items.map((item, index) => {
      const name = escapeHtml(item.title || 'Product');
      const detail = mode === 'quote' ? `Option: ${escapeHtml(item.variant || 'Standard')} · SKU: ${escapeHtml(item.sku || 'Not supplied')}` : 'Saved product';
      const qty = mode === 'quote' ? `<label>Quantity <input type="number" min="1" max="9999" value="${Math.min(9999, Math.max(1, Number(item.quantity) || 1))}" data-whl-quote-quantity="${index}" aria-label="Quote quantity for ${name}"></label>` : '';
      return `<div class="whl-card-list-item"><div><strong><a href="${escapeHtml(safeHref(item.url))}">${name}</a></strong><small>${detail}</small></div><div class="whl-card-list-item-actions">${qty}<button type="button" data-whl-remove="${index}">Remove</button></div></div>`;
    }).join('') : `<p>${mode === 'quote' ? 'No products in your quotation list yet.' : 'Your wishlist is empty.'}</p>`;
    const actions = $('[data-whl-dialog-actions]', el);
    const note = $('[data-whl-dialog-note]', el);
    actions.innerHTML = '';
    if (mode === 'quote') {
      const email = quoteEmail();
      const send = document.createElement('a');
      send.className = 'whl-card-dialog-primary';
      send.textContent = 'Prepare quote email';
      send.href = email && items.length ? `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent('Whitehouse quotation request')}&body=${encodeURIComponent(quoteBody())}` : '#';
      if (!email || !items.length) send.setAttribute('aria-disabled', 'true');
      actions.appendChild(send);
      const copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'whl-card-dialog-secondary';
      copy.dataset.whlCopyQuote = '';
      copy.textContent = 'Copy quote details';
      copy.disabled = !items.length;
      actions.appendChild(copy);
      note.textContent = email ? 'Your request is not sent until you send the prepared email. Saved only in this browser.' : 'Store contact email is not configured. Copy the quote details and contact the store. Saved only in this browser.';
    
} else {
  // View full wishlist button
  const viewWishlist = document.createElement('a');
  viewWishlist.className = 'whl-card-dialog-primary';
  viewWishlist.href = '/pages/wishlist';
  viewWishlist.textContent = 'View Full Wishlist';
  actions.appendChild(viewWishlist);

  // Customer-friendly guest wishlist notice
  note.replaceChildren();

  const message = document.createElement('span');
  message.textContent =
    'Your favourites are saved in this browser. ';

  const signIn = document.createElement('a');
  signIn.href = '/account/login';
  signIn.textContent = 'Sign in';
  signIn.style.fontWeight = '700';

  const ending = document.createElement('span');
  ending.textContent =
    ' to access your account. Your favourites will stay on this device until account syncing is enabled.';

  note.append(message, signIn, ending);
}

  }
  function openDialog(mode) {
    const el = ensureDialog();
    renderDialog(mode);
    if (!el.open && typeof el.showModal === 'function') el.showModal();
    else el.setAttribute('open', '');
  }
  function eventHandler(event) {
    const wish = event.target.closest('[data-whl-card-wish]');
    if (wish) { event.preventDefault(); event.stopPropagation(); const tile = wish.closest('[data-whl-card-actions]'); if (tile) toggleWishlist(tile); return; }
    const quote = event.target.closest('[data-whl-card-quote-add]');
    if (quote) { event.preventDefault(); event.stopPropagation(); const tile = quote.closest('[data-whl-card-actions]'); if (tile) addQuote(tile); return; }
    const view = event.target.closest('[data-whl-card-quote-view]');
    if (view) { event.preventDefault(); openDialog('quote'); return; }
    const wishView = event.target.closest('[data-whl-card-wishlist-view]');
    if (wishView) { event.preventDefault(); openDialog('wishlist'); return; }
    if (!dialog || !dialog.contains(event.target)) return;
    if (event.target.closest('[data-whl-dialog-close]')) { event.preventDefault(); dialog.close(); return; }
    const remove = event.target.closest('[data-whl-remove]');
    if (remove) {
      const mode = dialog.dataset.mode;
      const key = mode === 'quote' ? QKEY : WKEY;
      const idx = Number(remove.dataset.whlRemove);
      const list = read(key);
      if (Number.isInteger(idx) && idx >= 0 && idx < list.length) {
        list.splice(idx, 1); if (write(key, list)) { syncButtons(); renderDialog(mode); window.dispatchEvent(new CustomEvent(mode === 'quote' ? 'whl:quote-updated' : 'whl:wishlist-updated')); }
      }
    }
    const copy = event.target.closest('[data-whl-copy-quote]');
    if (copy) {
      event.preventDefault();
      const body = quoteBody();
      if (navigator.clipboard?.writeText) navigator.clipboard.writeText(body).then(() => toast('Quote details copied.')).catch(() => toast('Clipboard unavailable; use your email app.'));
      else toast('Clipboard unavailable; use your email app.');
    }
  }
  function changeHandler(event) {
    const qty = event.target.closest('[data-whl-quote-quantity]');
    if (!qty) return;
    const idx = Number(qty.dataset.whlQuoteQuantity), list = read(QKEY);
    if (!Number.isInteger(idx) || idx < 0 || idx >= list.length) return;
    list[idx].quantity = Math.min(9999, Math.max(1, Math.floor(Number(qty.value) || 1)));
    if (write(QKEY, list)) { syncButtons(); renderDialog('quote'); window.dispatchEvent(new CustomEvent('whl:quote-updated')); }
  }
  function init() {
    document.addEventListener('click', eventHandler);
    document.addEventListener('change', changeHandler);
    window.addEventListener('storage', event => { if (event.key === WKEY || event.key === QKEY) scheduleSync(); });
    document.addEventListener('shopify:section:load', scheduleSync);
    const observer = new MutationObserver(mutations => {
      if (mutations.some(m => Array.from(m.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('[data-whl-card-actions], [data-whl-card-saved-links]') || node.querySelector?.('[data-whl-card-actions]'))))) scheduleSync();
    });
    observer.observe(document.body, {childList: true, subtree: true});
    syncButtons();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
})();
